/* eslint-disable no-unused-vars */
import cs from 'classnames';
import dayjs from 'dayjs';

import React, { useEffect, useState } from 'react';
import {
    Table,
    Space,
    Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ENDED, STATUSES } from '../../const/booking.constants';
import { IBooking } from '../../interfaces/booking.interface';
import { EBookingStatus } from '../../enums/booking.enum';
import { getMinutes, getSeconds } from '../../utils/bookings';
import { Actions } from './Actions';
import { MultiPickingBadge } from './MultiPickingBadge';

const ACTIVE: string[] = [
    STATUSES.READY_FOR_PICKUP,
    STATUSES.ACCEPTED,
    STATUSES.ARRIVED_AT_PICKUP,
    STATUSES.WAY_TO_DELIVER,
    STATUSES.ARRIVED_AT_DELIVERY,
];

const { Text } = Typography;

export interface TableBookingsProps {
    loading: boolean;
    total: number;
    data: IBooking[];
    paginator: {
        currentPage: number;
        pages: number;
        pageSize: number;
    },
    showSort?: boolean
    refreshData: () => void;
    onPageChange?: (value: number) => void;
    onTableChange?: (sorter: any, pagination: any, filter: any) => void;
    columnsSelected?: string[];
}

const TableBookings = ({
    loading,
    total,
    data,
    paginator,
    refreshData,
    onTableChange,
    showSort = false,
    onPageChange,
    columnsSelected,
}: TableBookingsProps) => {
    const { t: tb } = useTranslation('bookings', { keyPrefix: 'TABLE' });
    const { t: ts } = useTranslation('bookings', { keyPrefix: 'STATUS' });
    const { t: to } = useTranslation('bookings', { keyPrefix: 'ORDER_FINISHED' });

    const { currentPage, pageSize } = paginator;

    const [isHistory, setIsHistory] = useState<boolean>(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setIsHistory(location.pathname.includes('history'));
    }, [location]);

    const columns = [
        {
            title: tb('TIME'),
            dataIndex: 'createdAt',
            key: 'timeElapsed',
            align: 'center' as 'center',
            width: 150,
            render: (timeElapsed: number, booking: IBooking) => {
                const minutes: number = getMinutes(booking);

                const elapsedTime = Math.round((booking?.elapsedTime || 0) / 60);
                const warning = isHistory ? elapsedTime >= 40 : minutes >= 40;
                const dangerous = isHistory ? elapsedTime > 60 : minutes > 60;

                return (
                    <div className={cs('table-time', { warning, dangerous, ended: ENDED.includes(booking.statusText) })}>
                        <Text>
                            {
                                isHistory ? (
                                    `${elapsedTime} min`
                                ) : (
                                    `${minutes} min`
                                )
                            }
                        </Text>
                    </div>
                );
            },
        },
        {
            title: tb('SERVICE_ID'),
            dataIndex: 'bookingNumericId',
            key: 'bookingNumericId',
            align: 'center' as 'center',
            render: (bookingNumericId: number, booking: IBooking) => (
                <Space size={4}>
                    <Text>
                        {bookingNumericId}
                    </Text>
                    {booking?.subtype === 'MULTI_PICKING' && <MultiPickingBadge />}
                </Space>
            ),
        },
        {
            title: tb('DATE'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as 'center',
            render: (createdAt: Date) => (
                <Space direction="vertical" size={4}>
                    <Text>
                        {dayjs(createdAt).format('DD/MM/YY')}
                    </Text>
                    <Text>
                        {dayjs(createdAt).format('HH:mm A')}
                    </Text>
                </Space>
            ),
        },
        {
            title: tb('STATUS'),
            dataIndex: 'statusText',
            key: 'statusText',
            align: 'center' as 'center',
            render: (statusText: EBookingStatus) => (
                <Text className={cs('table-status', statusText)}>
                    {ts(statusText)}
                </Text>
            ),
        },
        {
            title: tb('STORE'),
            dataIndex: 'businessName',
            key: 'businessName',
            align: 'center' as 'center',
            render: (businessName: string) => (
                <Text>
                    {businessName}
                </Text>
            ),
        },
        {
            title: tb('DRIVER'),
            dataIndex: 'driverName',
            key: 'driverName',
            width: 170,
            align: 'center' as 'center',
            render: (driverName: string, booking: IBooking) => (
                !driverName
                    ? (
                        <div className="table-status">
                            <Text>
                                {tb('NOT_ASSIGNED')}
                            </Text>
                        </div>
                    )
                    : (
                        <Text>
                            {driverName}
                        </Text>
                    )
            ),
        },
        {
            title: tb('PAYMENT_METHOD'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            align: 'center' as 'center',
            render: (paymentMethod: string) => (
                <Text>
                    {to(paymentMethod)}
                </Text>
            ),
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as 'center',
            render: (actions: string, record: IBooking) => (
                <Actions record={record} />
            ),
        },
    ];

    const visibleColumns = columnsSelected
        ? columns.filter((col) => col.key === 'actions' || columnsSelected.includes(col.key))
        : columns;

    const handleRowClick = (event: React.MouseEvent<HTMLElement>, record: IBooking) => {
        const element = event.target as HTMLElement;

        if (element.closest('.ant-dropdown-trigger')
            || element.closest('.ant-dropdown-menu')
            || element.closest('.ant-dropdown-menu-item')
            || element.closest('.ant-dropdown')) {
            return;
        }

        navigate(`/bookings/${record._id}`);
    };

    return (
        <Table
            className="table"
            dataSource={data}
            columns={visibleColumns}
            scroll={{ x: 1000 }}
            locale={{
                filterConfirm: 'Ok',
                filterReset: 'Reset',
                emptyText: tb('NO_DATA'),
            }}
            rowClassName={(row) => (ACTIVE.includes(row.statusText) ? 'tr-new' : '')}
            onChange={onTableChange}
            pagination={{
                current: currentPage,
                total,
                onChange: onPageChange,
                pageSize,
                showSizeChanger: true,
            }}
            rowKey="_id"
            loading={loading}
            onRow={(record, index) => ({
                onClick: (event) => handleRowClick(event, record),
            })}
        />
    );
};

export default TableBookings;
